// frontend/src/utils/analytics.ts
//
// Self-hosted web-analytics beacon. Page views -> POST /analytics/collect,
// custom events -> POST /analytics/event.
//
// CONSENT: tracking only fires when the user has granted analytics consent
// (contexts/CookieConsentContext.getAnalyticsConsent). No consent -> no beacon.
// Default is OFF until the user accepts via the cookie banner.
//
// IDENTITY: a random session id (sessionStorage, cleared on tab close) groups a
// visit. When logged in, the database user_id is attached so views/events can be
// attributed to the account. user_id is read from the 'user' JSON blob written
// by api.ts (the old code read a bare 'userId' key that was never set, so
// attribution was always null — fixed here).
//
// Fire-and-forget: never blocks navigation, never throws.
import { API_URL } from './api';
import { getAnalyticsConsent } from '../contexts/CookieConsentContext';

const SESSION_KEY = 'pc_sid';

function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const sid: string =
      (crypto as any)?.randomUUID?.() ??
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
    return sid;
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function getUserId(): string | undefined {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return parsed?.userId || undefined;
  } catch {
    return undefined;
  }
}

function post(endpoint: string, payload: Record<string, unknown>): void {
  try {
    if (!getAnalyticsConsent()) return; // consent gate — no consent, no tracking
    const body = JSON.stringify({
      ...payload,
      sessionId: getSessionId(),
      userId: getUserId(),
    });
    const url = `${API_URL}${endpoint}`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => { /* analytics must never surface an error */ });
    }
  } catch {
    /* never let analytics break navigation */
  }
}

/** Record a page view. Called on every route change. */
export function trackPageView(path: string): void {
  post('/analytics/collect', { path, referrer: document.referrer || undefined });
}

/**
 * Record a custom product event for funnel/feature analysis, e.g.
 *   trackEvent('trip_created', { tripId })
 *   trackEvent('photo_uploaded', { count: 3 })
 *   trackEvent('early_access_requested')
 * Same consent gate and session/user attribution as page views.
 */
export function trackEvent(name: string, props?: Record<string, unknown>): void {
  post('/analytics/event', { name, props: props || {}, path: window.location.pathname });
}
